import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { AY_ADLARI } from '../../constants/tarih';
import { useSearchParams } from 'react-router-dom';
import Header from '../../components/Header';
import { useAuth } from '../../auth/AuthProvider';
import { hasPermission, PERMISSIONS } from '../../auth/permissions';
import { useToast } from '../../components/Toast';
import { styles, colors, radius, shadows } from '../../styles/theme';
import { invalidateQueries } from '../../hooks/useCachedQuery';
import { normalizeDecimalInput, parseDecimal } from '../../utils/decimalInput';
import HizliPuantajModal from './HizliPuantajModal';
import { logError } from '../../utils/logError';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8001';

const DURUM_COLORS = {
  TASLAK: { bg: '#fff3e0', color: '#e65100' },
  KAPANDI: { bg: '#e3f2fd', color: '#1565c0' },
  ODENDI: { bg: '#e8f5e9', color: '#2e7d32' },
};

const thStyle = { padding: '6px 6px', textAlign: 'left', fontSize: 10, fontWeight: 700, color: '#555', borderBottom: '2px solid #ddd', whiteSpace: 'nowrap' };
const thStyleNum = { ...thStyle, textAlign: 'right' };
const tdStyle = { padding: '6px 6px', fontSize: 11, whiteSpace: 'nowrap' };
const tdStyleNum = { ...tdStyle, textAlign: 'right', fontFamily: 'monospace' };
const btnSmall = { padding: '4px 8px', fontSize: 11, border: '1px solid #ccc', background: 'white', borderRadius: 3, cursor: 'pointer' };


// Ok-navigasyonlu modern ay seçici. value/onChange: YYYY-MM string.
const MonthPicker = ({ value, onChange }) => {
  // value "YYYY-MM" → yıl + ay-index (0-11). Hatalı/boş değerde bugüne düş.
  const parse = (v) => {
    const matched = /^(\d{4})-(\d{2})$/.exec(v || '');
    if (matched) {
      const yil = parseInt(matched[1], 10);
      const ayIdx = parseInt(matched[2], 10) - 1;
      if (ayIdx >= 0 && ayIdx <= 11) return { yil, ayIdx };
    }
    const now = new Date();
    return { yil: now.getFullYear(), ayIdx: now.getMonth() };
  };
  const { yil, ayIdx } = parse(value);

  const [open, setOpen] = useState(false);
  // Popover'da gezilen yıl (seçili yıldan bağımsız ileri/geri gidilebilir)
  const [popYil, setPopYil] = useState(yil);
  const wrapRef = useRef(null);

  // value dışarıdan değişirse popover yılını senkronla
  useEffect(() => { setPopYil(yil); }, [yil]);

  // Dışarı tıklayınca kapat (nice-to-have)
  useEffect(() => {
    if (!open) return undefined;
    const handler = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  const emit = (y, idx) => onChange(`${y}-${String(idx + 1).padStart(2, '0')}`);

  // Bir ay ileri/geri — yıl devri ile
  const step = (delta) => {
    let y = yil;
    let idx = ayIdx + delta;
    if (idx < 0) { idx = 11; y -= 1; }
    else if (idx > 11) { idx = 0; y += 1; }
    emit(y, idx);
  };

  const selectMonth = (idx) => {
    emit(popYil, idx);
    setOpen(false);
  };

  const navBtn = {
    width: 30, height: 30, display: 'flex', alignItems: 'center', justifyContent: 'center',
    border: 'none', background: 'transparent', cursor: 'pointer', fontSize: 18, lineHeight: 1,
    color: colors.textSecondary, borderRadius: radius.sm, padding: 0,
  };

  return (
    <div ref={wrapRef} style={{ position: 'relative', display: 'inline-block' }}>
      {/* Pill: [‹]  Ay Yıl  [›] */}
      <div style={{ display: 'flex', alignItems: 'center', border: `1px solid ${colors.border}`, borderRadius: radius.sm, background: colors.surface, height: 38, overflow: 'hidden' }}>
        <button type="button" onClick={() => step(-1)} aria-label="Önceki ay" style={{ ...navBtn, height: 36 }}>‹</button>
        <button
          type="button"
          onClick={() => setOpen(o => !o)}
          aria-label="Ay seç"
          style={{ minWidth: 132, padding: '0 8px', height: 36, border: 'none', borderLeft: `1px solid ${colors.border}`, borderRight: `1px solid ${colors.border}`, background: 'transparent', cursor: 'pointer', fontSize: 14, fontWeight: 600, color: colors.textPrimary }}
        >
          {AY_ADLARI[ayIdx]} {yil}
        </button>
        <button type="button" onClick={() => step(1)} aria-label="Sonraki ay" style={{ ...navBtn, height: 36 }}>›</button>
      </div>

      {/* Popover: yıl satırı + 12 ay grid */}
      {open && (
        <div style={{ position: 'absolute', top: 'calc(100% + 6px)', left: 0, zIndex: 50, background: colors.surface, border: `1px solid ${colors.border}`, borderRadius: radius.md, boxShadow: shadows.lg, padding: 12, width: 280 }}>
          {/* Yıl satırı: ‹ 2026 › */}
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
            <button type="button" onClick={() => setPopYil(y => y - 1)} aria-label="Önceki yıl" style={navBtn}>‹</button>
            <span style={{ fontSize: 15, fontWeight: 700, color: colors.textPrimary }}>{popYil}</span>
            <button type="button" onClick={() => setPopYil(y => y + 1)} aria-label="Sonraki yıl" style={navBtn}>›</button>
          </div>
          {/* 12 ay — 3 sütun x 4 satır */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 6 }}>
            {AY_ADLARI.map((ad, idx) => {
              const secili = idx === ayIdx && popYil === yil;
              return (
                <button
                  key={ad}
                  type="button"
                  onClick={() => selectMonth(idx)}
                  style={{
                    padding: '8px 4px', fontSize: 13, cursor: 'pointer', borderRadius: radius.sm,
                    border: `1px solid ${secili ? colors.success : colors.border}`,
                    background: secili ? colors.success : colors.surface,
                    color: secili ? colors.textInverse : colors.textPrimary,
                    fontWeight: secili ? 700 : 500,
                  }}
                >
                  {ad}
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

const PuantajAylikPage = () => {
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  const toast = useToast();
  const canManage = user && hasPermission(user.role, PERMISSIONS.MANAGE_HR, user.effectivePermissions);

  const [ay, setAy] = useState(() => {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
  });
  const [lokFilter, setLokFilter] = useState('');
  const [durumFilter, setDurumFilter] = useState('');
  const [puantajlar, setPuantajlar] = useState([]);
  const [personeller, setPersoneller] = useState([]);
  const [lokasyonlar, setLokasyonlar] = useState([]);
  const [loading, setLoading] = useState(true);
  // Faz D: ciro payı global bayrağı askıda olabilir → Ciro kolonu gizlenir
  const [ciroPayiAktif, setCiroPayiAktif] = useState(false);
  // Ek mesai çarpanı (İK Ayarlar config; default 1.0 = saat karşılığı)
  const [fazlaMesaiCarpani, setFazlaMesaiCarpani] = useState(1.0);

  // Inline edit state
  const [editingCell, setEditingCell] = useState(null); // {id, field}
  const [editValue, setEditValue] = useState('');
  // Excel benzeri satır izleme: imlecin üzerinde olduğu satırı vurgula (uzak sütuna
  // giderken hangi satırda olduğunu görmek için)
  const [hoverRowId, setHoverRowId] = useState(null);

  // Detay paneli
  const [selectedId, setSelectedId] = useState(null);

  // Yeni satır modal
  const [newModalOpen, setNewModalOpen] = useState(false);

  // Avans modal (tarih bazlı detay)
  const [avansModalPuantaj, setAvansModalPuantaj] = useState(null);

  // Hızlı toplu giriş modalı
  const [hizliModalOpen, setHizliModalOpen] = useState(false);

  // Faz F Task 5: toplu silme — seçili TASLAK puantaj id'leri
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  // Başlık checkbox'ı için ref — kısmi seçimde indeterminate (React prop desteklemez)
  const headerCbRef = useRef(null);

  // Görünüm modu kaldırıldı — tüm kolonlar sıkıştırılmış font/padding ile görüntülenir
  const viewMode = 'full';

  const token = localStorage.getItem('access_token');

  const fetchData = useCallback(async () => {
    setLoading(true);
    setSelectedIds(new Set()); // ay/lokasyon/durum filtresi ya da yenileme → seçimi temizle
    const authHeaders = { 'Authorization': `Bearer ${token}` };
    try {
      const qs = new URLSearchParams({ ay });
      if (lokFilter) qs.set('lokasyon_id', lokFilter);
      if (durumFilter) qs.set('durum', durumFilter);
      const personelIdFromUrl = searchParams.get('personel_id');
      if (personelIdFromUrl) qs.set('personel_id', personelIdFromUrl);

      const [puaRes, persRes, lokRes] = await Promise.all([
        fetch(`${API_URL}/api/puantaj?${qs}`, { headers: authHeaders }),
        // Issue 1: seçili ayda çalışanları getir (aktif olanları değil) — geçmiş ay puantajı doğru personel listesi
        fetch(`${API_URL}/api/personel?calisan_ay=${ay}`, { headers: authHeaders }),
        fetch(`${API_URL}/api/lokasyonlar`, { headers: authHeaders }),
      ]);
      if (puaRes.ok) setPuantajlar(await puaRes.json());
      else setPuantajlar([]);
      if (persRes.ok) setPersoneller(await persRes.json());
      if (lokRes.ok) setLokasyonlar((await lokRes.json()).filter(l => l.aktif !== false));
    } catch (err) {
      logError('Puantaj yükleme hatası:', err);
    }
    setLoading(false);
  }, [ay, lokFilter, durumFilter, searchParams, token]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // Faz D: ciro payı global bayrağını + ek mesai çarpanını çek
  useEffect(() => {
    fetch(`${API_URL}/api/ik/ayarlar`, { headers: { 'Authorization': `Bearer ${token}` } })
      .then(r => r.ok ? r.json() : null)
      .then(d => {
        if (!d) return;
        setCiroPayiAktif(!!d.ciro_payi_aktif);
        if (d.fazla_mesai_carpani != null) setFazlaMesaiCarpani(Number(d.fazla_mesai_carpani));
      })
      .catch(() => {});
  }, [token]);

  // KPI hesabı
  const kpi = useMemo(() => {
    const toplam_brut = puantajlar.reduce((s, p) => s + (p.toplam_hakkedis || 0), 0);
    const bankadan = puantajlar.reduce((s, p) => s + (p.resmi_odeme || 0), 0);
    const elden = puantajlar.reduce((s, p) => s + (p.nihai_odeme || 0), 0);
    const avans = puantajlar.reduce((s, p) => s + (p.avans || 0), 0);
    const kapanan = puantajlar.filter(p => p.durum === 'KAPANDI' || p.durum === 'ODENDI').length;
    return { toplam_brut, bankadan, elden, avans, kapanan, toplam: puantajlar.length };
  }, [puantajlar]);

  const fmt = (n) => (n || 0).toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  const fmtK = (n) => (n || 0).toLocaleString('tr-TR', { maximumFractionDigits: 0 });

  const handleCellEdit = async (id, field, value) => {
    const numValue = parseDecimal(value, 0);
    try {
      const r = await fetch(`${API_URL}/api/puantaj/${id}`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ [field]: numValue }),
      });
      if (r.ok) {
        const updated = await r.json();
        setPuantajlar(prev => prev.map(p => p.id === id ? updated : p));
        toast.success('Güncellendi');
      } else {
        const err = await r.json().catch(() => ({}));
        toast.error(err.detail || 'Güncellenemedi');
      }
    } catch {
      toast.error('Bağlantı hatası');
    } finally {
      setEditingCell(null);
      setEditValue('');
    }
  };

  const handleDelete = async (p) => {
    if (!window.confirm(`${p.personel_ad} ${p.ay} puantajı silinecek. Emin misin?`)) return;
    try {
      const r = await fetch(`${API_URL}/api/puantaj/${p.id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` },
      });
      if (r.ok) { toast.success('Silindi'); fetchData(); }
      else {
        const err = await r.json().catch(() => ({}));
        toast.error(err.detail || 'Silinemedi');
      }
    } catch {
      toast.error('Bağlantı hatası');
    }
  };

  const handleBordro = (p) => {
    const tip = window.prompt("Bordro tipi: D = Detaylı (Excel birebir) · R = Resmi (Banka görünümü)", "D");
    if (!tip) return;
    const tipParam = tip.toUpperCase().startsWith('R') ? 'RESMI' : 'DETAYLI';
    // PDF'yi yeni tab'da aç (inline Content-Disposition)
    const url = `${API_URL}/api/puantaj/${p.id}/bordro?tip=${tipParam}`;
    // Authorization header'la fetch yapıp blob olarak aç
    fetch(url, { headers: { 'Authorization': `Bearer ${token}` } })
      .then(r => {
        if (!r.ok) throw new Error('PDF üretilemedi');
        return r.blob();
      })
      .then(blob => {
        const blobUrl = URL.createObjectURL(blob);
        window.open(blobUrl, '_blank');
        setTimeout(() => URL.revokeObjectURL(blobUrl), 60000);
      })
      .catch(e => toast.error(e.message || 'Bordro hatası'));
  };

  const handleIzinYenile = async (p) => {
    try {
      const r = await fetch(`${API_URL}/api/puantaj/${p.id}/izinleri-uygula`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
      });
      if (r.ok) {
        const d = await r.json();
        // Puantajı yeniden çek (izin_ozet + calisma_gun güncel gelsin)
        const pr = await fetch(`${API_URL}/api/puantaj/${p.id}`, { headers: { 'Authorization': `Bearer ${token}` } });
        if (pr.ok) {
          const updated = await pr.json();
          setPuantajlar(prev => prev.map(x => x.id === p.id ? updated : x));
        }
        const delta = d.yeni_calisma_gun - d.eski_calisma_gun;
        toast.success(`Güncellendi: çal.gün ${d.eski_calisma_gun} → ${d.yeni_calisma_gun}${delta !== 0 ? ` (${delta > 0 ? '+' : ''}${delta})` : ''}`);
      } else {
        const err = await r.json().catch(() => ({}));
        toast.error(err.detail || 'Yenilenemedi');
      }
    } catch {
      toast.error('Bağlantı hatası');
    }
  };

  const handleAction = async (p, action) => {
    // Kesinleştir'de gidere YAZILAN tutar tam maliyettir (brüt hakediş = resmi + elden),
    // sadece elden değil — resmi de işletme kasasından çıkar.
    const tamMaliyet = p.tam_maliyet != null ? p.tam_maliyet : ((p.toplam_hakkedis || 0) + (p.bayram_izin_prim || 0));
    const confirmMsg = {
      kapat: `${p.personel_ad} için puantaj KESİNLEŞTİRİLECEK ve PERSONEL giderine ${tamMaliyet.toLocaleString('tr-TR')} ₺ tam maliyet yazılacak (resmi/banka ${(p.resmi_odeme || 0).toLocaleString('tr-TR')} + elden/nakit ${(p.nihai_odeme || 0).toLocaleString('tr-TR')}). Emin misin?`,
      ode: `${p.personel_ad} için puantaj ÖDENDİ olarak işaretlenecek. Emin misin?`,
      ac: `${p.personel_ad} için puantaj TASLAK'a çekilecek ve ilgili gider kaydı silinecek. Emin misin?`,
    }[action];
    if (!window.confirm(confirmMsg)) return;
    try {
      const r = await fetch(`${API_URL}/api/puantaj/${p.id}/${action}`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
      });
      if (r.ok) {
        const updated = await r.json();
        setPuantajlar(prev => prev.map(x => x.id === p.id ? updated : x));
        toast.success({ kapat: `Kapatıldı — ${tamMaliyet.toLocaleString('tr-TR')} ₺ tam maliyet gider yazıldı`, ode: 'Ödendi olarak işaretlendi', ac: 'Açıldı' }[action]);
        invalidateQueries(['gider', 'finance']);
      } else {
        const err = await r.json().catch(() => ({}));
        toast.error(err.detail || 'Hata');
      }
    } catch {
      toast.error('Bağlantı hatası');
    }
  };

  const handleCsvExport = () => {
    // Faz D: ciro payı askıdaysa Ciro sütunu hem başlıktan hem satırdan çıkar
    const headers_csv = [
      'Personel', 'Cal.Gun', 'Gelinen Gün', 'Maas', 'Yol', 'Resmi',
      ...(ciroPayiAktif ? ['Ciro'] : []),
      'Avans', 'Kredi', 'Kul.Izin Saat', 'Kul.Izin Parasi', 'Borc Kes.', 'Bayram', 'Toplam Hakedis', 'Tam Maliyet', 'Toplam', 'Odenecek Net', 'Nihai Ode', 'Durum',
    ];
    const rows = puantajlar.map(p => [
      p.personel_ad, p.calisma_gun, p.gelinen_gun, p.aylik_brut_maas, p.yol_yardimi,
      p.resmi_odeme,
      ...(ciroPayiAktif ? [p.ciro_payi] : []),
      p.avans, p.kredi_kesintisi,
      p.kullanilmayan_izin_saat, (p.kullanilmayan_izin_parasi || 0).toFixed(2),
      p.borc_kesintisi, p.bayram_izin_prim, (p.toplam_hakkedis || 0).toFixed(2),
      (p.tam_maliyet ?? ((p.toplam_hakkedis || 0) + (p.bayram_izin_prim || 0))).toFixed(2),
      (p.toplam || 0).toFixed(2), (p.odenecek_net || 0).toFixed(2), (p.nihai_odeme || 0).toFixed(2), p.durum,
    ].map(v => String(v == null ? '' : v).replace(/"/g, '""')).join(';'));
    const csv = '\ufeff' + [headers_csv.join(';'), ...rows].join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `puantaj-${ay}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  // Faz F Task 5: toplu silme yardımcıları
  const taslakIds = useMemo(() => puantajlar.filter(p => p.durum === 'TASLAK').map(p => p.id), [puantajlar]);
  const allTaslakSelected = taslakIds.length > 0 && taslakIds.every(id => selectedIds.has(id));

  // Kısmi seçimde başlık checkbox'ı indeterminate görünsün (bazıları seçili ama hepsi değil)
  useEffect(() => {
    if (headerCbRef.current) {
      headerCbRef.current.indeterminate = selectedIds.size > 0 && !allTaslakSelected;
    }
  }, [selectedIds, allTaslakSelected]);

  const toggleRow = (id) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const toggleAll = () => {
    setSelectedIds(prev => {
      // Görünen tüm TASLAK satırlar seçiliyse hepsini kaldır, değilse hepsini ekle
      const allSelected = taslakIds.length > 0 && taslakIds.every(id => prev.has(id));
      return allSelected ? new Set() : new Set(taslakIds);
    });
  };

  const handleTopluSil = async () => {
    const ids = [...selectedIds];
    if (ids.length === 0) return;
    if (!window.confirm(`${ids.length} taslak puantaj silinecek. Emin misin?`)) return;
    try {
      const r = await fetch(`${API_URL}/api/puantaj/toplu-sil`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ ids }),
      });
      if (r.ok) {
        const d = await r.json();
        const silinen = d.silinen || 0;
        const atlanan = d.atlanan || 0;
        toast.success(`${silinen} silindi${atlanan ? `, ${atlanan} atlandı` : ''}`);
        setSelectedIds(new Set());
        fetchData();
      } else {
        const err = await r.json().catch(() => ({}));
        toast.error(err.detail || 'Silinemedi');
      }
    } catch {
      toast.error('Bağlantı hatası');
    }
  };

  // Cell component
  const EditableCell = ({ puantaj, field }) => {
    const isEditing = editingCell && editingCell.id === puantaj.id && editingCell.field === field;
    const locked = puantaj.durum === 'KAPANDI' || puantaj.durum === 'ODENDI' || !canManage;
    const value = puantaj[field];

    if (isEditing) {
      // type="number" DEĞİL: Safari/WebKit'te ara/virgüllü ondalıkta (".", "5,")
      // .value="" dönüp kontrollü reset alanı siliyordu (Gülten Hanım Safari bug'ı).
      // type=text + inputMode=decimal: mobil/tablette sayısal klavye, virgül/nokta
      // serbest, metin asla silinmez. normalizeDecimalInput virgülü noktaya çevirir.
      return (
        <input
          autoFocus
          type="text"
          inputMode="decimal"
          value={editValue}
          onChange={e => setEditValue(normalizeDecimalInput(e.target.value))}
          onBlur={() => handleCellEdit(puantaj.id, field, editValue)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') handleCellEdit(puantaj.id, field, editValue);
            if (e.key === 'Escape') { setEditingCell(null); setEditValue(''); }
          }}
          style={{ width: 70, padding: 3, fontSize: 11, border: '2px solid #1565c0', borderRadius: 3 }}
        />
      );
    }
    return (
      <span
        style={{ cursor: locked ? 'not-allowed' : 'pointer', color: locked ? '#999' : 'inherit', display: 'inline-block', minWidth: 50, padding: '2px 3px' }}
        onClick={() => { if (!locked) { setEditingCell({ id: puantaj.id, field }); setEditValue(value == null ? '' : String(value)); } }}
      >
        {fmtK(value)}
      </span>
    );
  };

  const selected = puantajlar.find(p => p.id === selectedId);

  if (!canManage) {
    return (
      <div>
        <Header title="Aylık Puantaj" />
        <div style={{ padding: 40, textAlign: 'center' }}>Yetkiniz yok.</div>
      </div>
    );
  }

  return (
    <div>
      <Header title="Aylık Puantaj" />
      <div style={{ padding: 20, background: colors.background, minHeight: 'calc(100vh - 60px)' }}>

        {/* Filtre bar */}
        <div style={{ background: colors.surface, padding: 14, borderRadius: radius.lg, marginBottom: 14, display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap', boxShadow: shadows.sm }}>
          <MonthPicker value={ay} onChange={setAy} />
          <select value={lokFilter} onChange={e => setLokFilter(e.target.value)} style={{ padding: '8px 12px', border: `1px solid ${colors.border}`, borderRadius: radius.sm, fontSize: 14 }}>
            <option value="">Tüm Lokasyonlar</option>
            {lokasyonlar.map(l => <option key={l.id} value={l.id}>{l.ad}</option>)}
          </select>
          <select value={durumFilter} onChange={e => setDurumFilter(e.target.value)} style={{ padding: '8px 12px', border: `1px solid ${colors.border}`, borderRadius: radius.sm, fontSize: 14 }}>
            <option value="">Tüm Durumlar</option>
            <option value="TASLAK">Taslak</option>
            <option value="KAPANDI">Kapandı</option>
            <option value="ODENDI">Ödendi</option>
          </select>
          <span style={{ fontSize: 13, color: '#888' }}>
            {puantajlar.length} satır · {fmtK(kpi.elden)} ₺ toplam
          </span>
          <div style={{ marginLeft: 'auto', display: 'flex', gap: 8, alignItems: 'center' }}>
            {canManage && selectedIds.size > 0 && (
              <button onClick={handleTopluSil} style={{ padding: '8px 14px', background: '#c62828', color: 'white', border: 'none', borderRadius: radius.sm, cursor: 'pointer', fontSize: 13, fontWeight: 600 }}>
                Seçilenleri Sil ({selectedIds.size})
              </button>
            )}
            <button onClick={handleCsvExport} style={{ padding: '8px 14px', background: '#2e7d32', color: 'white', border: 'none', borderRadius: radius.sm, cursor: 'pointer', fontSize: 13 }}>CSV İndir</button>
            <button onClick={() => setHizliModalOpen(true)} disabled={!canManage} style={{ padding: '8px 16px', background: '#7b1fa2', color: 'white', border: 'none', borderRadius: radius.sm, cursor: canManage ? 'pointer' : 'not-allowed', fontWeight: 600, fontSize: 14, opacity: canManage ? 1 : 0.5 }} title="Lokasyon bazlı tüm personele aynı anda hızlı giriş">⚡ Hızlı Toplu Giriş</button>
            <button onClick={() => setNewModalOpen(true)} disabled={!canManage} style={{ padding: '8px 16px', background: styles.primary, color: 'white', border: 'none', borderRadius: radius.sm, cursor: canManage ? 'pointer' : 'not-allowed', fontWeight: 600, fontSize: 14, opacity: canManage ? 1 : 0.5 }}>+ Tek Satır</button>
          </div>
        </div>

        {/* KPI */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 10, marginBottom: 14 }}>
          <div style={{ background: colors.surface, padding: 12, borderRadius: radius.md, boxShadow: shadows.sm, borderLeft: '3px solid #1565c0' }}>
            <div style={{ fontSize: 11, color: '#666' }}>Toplam Hakediş</div>
            <div style={{ fontSize: 18, fontWeight: 700 }}>{fmtK(kpi.toplam_brut)} ₺</div>
          </div>
          <div style={{ background: colors.surface, padding: 12, borderRadius: radius.md, boxShadow: shadows.sm, borderLeft: '3px solid #2e7d32' }}>
            <div style={{ fontSize: 11, color: '#666' }}>Bankadan (Resmi)</div>
            <div style={{ fontSize: 18, fontWeight: 700 }}>{fmtK(kpi.bankadan)} ₺</div>
          </div>
          <div style={{ background: colors.surface, padding: 12, borderRadius: radius.md, boxShadow: shadows.sm, borderLeft: '3px solid #e65100' }}>
            <div style={{ fontSize: 11, color: '#666' }}>Elden (Nihai)</div>
            <div style={{ fontSize: 18, fontWeight: 700 }}>{fmtK(kpi.elden)} ₺</div>
          </div>
          <div style={{ background: colors.surface, padding: 12, borderRadius: radius.md, boxShadow: shadows.sm, borderLeft: '3px solid #6a1b9a' }}>
            <div style={{ fontSize: 11, color: '#666' }}>Avans</div>
            <div style={{ fontSize: 18, fontWeight: 700 }}>{fmtK(kpi.avans)} ₺</div>
          </div>
          <div style={{ background: colors.surface, padding: 12, borderRadius: radius.md, boxShadow: shadows.sm, borderLeft: '3px solid #455a64' }}>
            <div style={{ fontSize: 11, color: '#666' }}>Kapanan</div>
            <div style={{ fontSize: 18, fontWeight: 700 }}>{kpi.kapanan} / {kpi.toplam}</div>
          </div>
        </div>

        {/* Ana içerik: sol tablo + sağ detay */}
        <div style={{ display: 'flex', gap: 14 }}>
          <div style={{ flex: selectedId ? '0 0 62%' : 1, minWidth: 0, background: colors.surface, borderRadius: radius.md, boxShadow: shadows.sm, overflow: 'hidden' }}>
            {loading ? <div style={{ padding: 40, textAlign: 'center' }}>Yükleniyor...</div>
             : puantajlar.length === 0 ? <div style={{ padding: 40, textAlign: 'center', color: '#888' }}>Bu ay için puantaj kaydı yok. "+ Yeni Satır" ile başlayın.</div>
             : (
              <div style={{ overflowX: 'auto', width: '100%' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 11, tableLayout: 'auto' }}>
                  <thead>
                    <tr style={{ background: '#f5f5f5' }}>
                      {canManage && (
                        <th style={{ ...thStyle, textAlign: 'center', width: 28 }}>
                          <input
                            ref={headerCbRef}
                            type="checkbox"
                            checked={allTaslakSelected}
                            disabled={taslakIds.length === 0}
                            onChange={toggleAll}
                            title="Tüm taslakları seç / kaldır"
                            style={{ cursor: taslakIds.length === 0 ? 'not-allowed' : 'pointer' }}
                          />
                        </th>
                      )}
                      <th style={thStyle}>Personel</th>
                      <th style={thStyleNum}>Çal.Gün</th>
                      <th style={thStyleNum} title="İşe gelinen gün sayısı. Yol yardımı = Gelinen Gün × günlük yol ücreti.">Gelinen Gün</th>
                      <th style={thStyleNum}>Maaş</th>
                      {viewMode === 'full' && <th style={thStyleNum}>Yol</th>}
                      <th style={thStyleNum}>Resmi</th>
                      {viewMode === 'full' && ciroPayiAktif && <th style={thStyleNum}>Ciro</th>}
                      <th style={thStyleNum} title="Ayın 15'inde elden verilen nakit avans. Sistem ayrı gider kaydı oluşturmaz — ay kapandığında bu tutar nihai ödemeden düşülür."><abbr style={{ textDecoration: 'none', borderBottom: '1px dotted #999' }}>Avans ⓘ</abbr></th>
                      {viewMode === 'full' && <th style={thStyleNum}>Kredi</th>}
                      {viewMode === 'full' && <th style={thStyleNum} title="Kullanılmayan izin — SAAT bazlı. Karşılık = aylık brüt / (30 × günlük çalışma saati) × saat.">Kul.İz.(s)</th>}
                      {viewMode === 'full' && <th style={thStyleNum}>Borç Kes.</th>}
                      {viewMode === 'full' && <th style={thStyleNum}>Bayram</th>}
                      <th style={{ ...thStyleNum, background: '#e8f5e9' }}>Nihai Öde</th>
                      <th style={thStyle}>Durum</th>
                      <th style={thStyle}>Aksiyon</th>
                    </tr>
                  </thead>
                  <tbody>
                    {puantajlar.map(p => {
                      const dc = DURUM_COLORS[p.durum] || DURUM_COLORS.TASLAK;
                      const locked = p.durum !== 'TASLAK';
                      const nihaiColor = (p.nihai_odeme || 0) < 0 ? '#c62828' : '#2e7d32';
                      // Excel benzeri: o an düzenlenen (uzak sütun da olsa) satırı vurgula —
                      // kullanıcı hangi satırda olduğunu görsün.
                      const isEditingRow = editingCell && editingCell.id === p.id;
                      return (
                        <tr
                          key={p.id}
                          onMouseEnter={() => setHoverRowId(p.id)}
                          onMouseLeave={() => setHoverRowId(prev => (prev === p.id ? null : prev))}
                          style={{
                            borderBottom: '1px solid #f0f0f0',
                            background: isEditingRow ? '#fff8e1'
                              : (selectedId === p.id ? '#f0f8ff'
                              : (hoverRowId === p.id ? '#f1f8ff'
                              : (locked ? '#fafafa' : 'white'))),
                            boxShadow: isEditingRow ? 'inset 3px 0 0 0 #f9a825' : undefined,
                          }}
                        >
                          {canManage && (
                            <td style={{ ...tdStyle, textAlign: 'center' }}>
                              <input
                                type="checkbox"
                                checked={selectedIds.has(p.id)}
                                disabled={p.durum !== 'TASLAK'}
                                onChange={() => toggleRow(p.id)}
                                title={p.durum === 'TASLAK' ? 'Toplu silme için seç' : 'Yalnız taslaklar toplu silinebilir'}
                                style={{ cursor: p.durum === 'TASLAK' ? 'pointer' : 'not-allowed' }}
                              />
                            </td>
                          )}
                          <td style={tdStyle}>{p.personel_ad}</td>
                          <td style={tdStyleNum}>
                            <EditableCell puantaj={p} field="calisma_gun" />
                            {p.izin_ozet && (p.izin_ozet.ucretsiz_gun > 0 || p.izin_ozet.yillik_gun > 0 || p.izin_ozet.mazeret_gun > 0 || p.izin_ozet.rapor_gun > 0) && (
                              <span
                                title={`İzin kayıtlarından otomatik: ${[
                                  p.izin_ozet.yillik_gun > 0 && `${p.izin_ozet.yillik_gun} YILLIK`,
                                  p.izin_ozet.ucretsiz_gun > 0 && `${p.izin_ozet.ucretsiz_gun} ÜCRETSİZ`,
                                  p.izin_ozet.mazeret_gun > 0 && `${p.izin_ozet.mazeret_gun} MAZERET`,
                                  p.izin_ozet.rapor_gun > 0 && `${p.izin_ozet.rapor_gun} RAPOR`,
                                ].filter(Boolean).join(', ')}. Manuel değiştirebilirsiniz.`}
                                style={{ marginLeft: 3, fontSize: 10, color: '#1565c0', cursor: 'help' }}
                              >
                                ⓘ
                              </span>
                            )}
                          </td>
                          <td style={tdStyleNum}><EditableCell puantaj={p} field="gelinen_gun" /></td>
                          <td style={tdStyleNum}><EditableCell puantaj={p} field="aylik_brut_maas" /></td>
                          {viewMode === 'full' && <td style={tdStyleNum}><EditableCell puantaj={p} field="yol_yardimi" /></td>}
                          <td style={tdStyleNum}><EditableCell puantaj={p} field="resmi_odeme" /></td>
                          {viewMode === 'full' && ciroPayiAktif && <td style={tdStyleNum}><EditableCell puantaj={p} field="ciro_payi" /></td>}
                          <td style={tdStyleNum}>
                            <span
                              onClick={() => { if (p.durum === 'TASLAK') setAvansModalPuantaj(p); }}
                              title={p.durum === 'TASLAK' ? 'Avans detayı — tarih bazlı giriş' : 'Kapalı'}
                              style={{ cursor: p.durum === 'TASLAK' ? 'pointer' : 'not-allowed', color: p.durum !== 'TASLAK' ? '#999' : (p.avans > 0 ? '#6a1b9a' : 'inherit'), textDecoration: p.durum === 'TASLAK' ? 'underline dotted' : 'none' }}
                            >
                              {fmtK(p.avans)}
                            </span>
                          </td>
                          {viewMode === 'full' && <td style={tdStyleNum}><EditableCell puantaj={p} field="kredi_kesintisi" /></td>}
                          {viewMode === 'full' && <td style={tdStyleNum}><EditableCell puantaj={p} field="kullanilmayan_izin_saat" /></td>}
                          {viewMode === 'full' && <td style={tdStyleNum}><EditableCell puantaj={p} field="borc_kesintisi" /></td>}
                          {viewMode === 'full' && <td style={tdStyleNum}><EditableCell puantaj={p} field="bayram_izin_prim" /></td>}
                          <td style={{ ...tdStyleNum, fontWeight: 700, color: nihaiColor, background: '#f0f8f0' }}>{fmt(p.nihai_odeme)}</td>
                          <td style={tdStyle}>
                            <span style={{ padding: '3px 10px', borderRadius: 12, fontSize: 10, fontWeight: 700, background: dc.bg, color: dc.color }}>{p.durum}</span>
                          </td>
                          <td style={tdStyle}>
                            <div style={{ display: 'flex', gap: 4 }}>
                              <button onClick={() => setSelectedId(selectedId === p.id ? null : p.id)} style={btnSmall}>Detay</button>
                              {p.durum === 'TASLAK' && (
                                <>
                                  <button onClick={() => handleIzinYenile(p)} title="İzin kayıtlarından çalışma gününü yeniden hesapla (manuel değer üzerine yazılır)" style={{ ...btnSmall, background: '#e1f5fe', color: '#01579b', borderColor: '#81d4fa' }}>🏖️ Yenile</button>
                                  <button onClick={() => handleAction(p, 'kapat')} style={{ ...btnSmall, background: '#1565c0', color: 'white', borderColor: '#1565c0' }}>Kesinleştir</button>
                                  <button onClick={() => handleBordro(p)} style={{ ...btnSmall, background: '#f3e5f5', color: '#6a1b9a', borderColor: '#ce93d8' }}>Bordro</button>
                                  <button onClick={() => handleDelete(p)} style={{ ...btnSmall, background: '#f8d7da', color: '#721c24', borderColor: '#f5c6cb' }}>Sil</button>
                                </>
                              )}
                              {p.durum === 'KAPANDI' && (
                                <>
                                  <button onClick={() => handleAction(p, 'ode')} style={{ ...btnSmall, background: '#2e7d32', color: 'white', borderColor: '#2e7d32' }}>Ödendi İşaretle</button>
                                  <button onClick={() => handleAction(p, 'ac')} style={{ ...btnSmall, background: '#fbc02d', color: '#333', borderColor: '#fbc02d' }}>Aç</button>
                                  <button onClick={() => handleBordro(p)} style={{ ...btnSmall, background: '#f3e5f5', color: '#6a1b9a', borderColor: '#ce93d8' }}>Bordro</button>
                                </>
                              )}
                              {p.durum === 'ODENDI' && (
                                <button onClick={() => handleBordro(p)} style={{ ...btnSmall, background: '#f3e5f5', color: '#6a1b9a', borderColor: '#ce93d8' }}>Bordro</button>
                              )}
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                  {puantajlar.length > 0 && (
                    <tfoot>
                      <tr style={{ background: '#fafafa', fontWeight: 700 }}>
                        {canManage && <td style={tdStyle}></td>}
                        <td style={tdStyle}>TOPLAM ({puantajlar.length})</td>
                        <td style={tdStyleNum}>-</td>
                        <td style={tdStyleNum}>-</td>
                        <td style={tdStyleNum}>{fmtK(puantajlar.reduce((s, p) => s + (p.aylik_brut_maas || 0), 0))}</td>
                        {viewMode === 'full' && <td style={tdStyleNum}>{fmtK(puantajlar.reduce((s, p) => s + (p.yol_yardimi || 0), 0))}</td>}
                        <td style={tdStyleNum}>{fmtK(kpi.bankadan)}</td>
                        {viewMode === 'full' && ciroPayiAktif && <td style={tdStyleNum}>{fmtK(puantajlar.reduce((s, p) => s + (p.ciro_payi || 0), 0))}</td>}
                        <td style={tdStyleNum}>{fmtK(kpi.avans)}</td>
                        {viewMode === 'full' && <td style={tdStyleNum}>{fmtK(puantajlar.reduce((s, p) => s + (p.kredi_kesintisi || 0), 0))}</td>}
                        {viewMode === 'full' && <td style={tdStyleNum}>{fmtK(puantajlar.reduce((s, p) => s + (p.kullanilmayan_izin_saat || 0), 0))}</td>}
                        {viewMode === 'full' && <td style={tdStyleNum}>{fmtK(puantajlar.reduce((s, p) => s + (p.borc_kesintisi || 0), 0))}</td>}
                        {viewMode === 'full' && <td style={tdStyleNum}>{fmtK(puantajlar.reduce((s, p) => s + (p.bayram_izin_prim || 0), 0))}</td>}
                        <td style={{ ...tdStyleNum, background: '#e8f5e9', color: '#2e7d32' }}>{fmtK(kpi.elden)}</td>
                        <td style={tdStyle}>-</td>
                        <td style={tdStyle}>-</td>
                      </tr>
                    </tfoot>
                  )}
                </table>
              </div>
            )}
          </div>

          {/* Detay panel */}
          {selectedId && selected && (
            <div style={{ flex: '0 0 37%', background: colors.surface, borderRadius: radius.md, boxShadow: shadows.sm, padding: 18, position: 'sticky', top: 80, alignSelf: 'flex-start' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 14 }}>
                <h3 style={{ margin: 0, fontSize: 16 }}>{selected.personel_ad}</h3>
                <button onClick={() => setSelectedId(null)} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 18, color: '#999' }}>×</button>
              </div>
              <div style={{ fontSize: 12, color: '#666', marginBottom: 14 }}>{selected.ay} · {selected.lokasyon_ad || 'Lokasyon yok'}</div>

              <div style={{ fontSize: 12, lineHeight: 1.8, color: '#333' }}>
                <div><strong>Kullanılmayan İzin Parası</strong>: {fmt(selected.kullanilmayan_izin_parasi)} ₺ ({selected.kullanilmayan_izin_saat || 0} saat)</div>
                <div><strong>Toplam Hakediş</strong>: Maaş×gün/30 + Yol + Ciro + Kul.İzin + Mesai = <strong>{fmt(selected.toplam_hakkedis)} ₺</strong></div>
                <div><strong>Toplam</strong>: Hakediş − Resmi − Avans − Kredi = <strong>{fmt(selected.toplam)} ₺</strong></div>
                <div><strong>Ödenecek Net</strong>: Toplam − Borç Kes. = <strong>{fmt(selected.odenecek_net)} ₺</strong></div>
                <div style={{ fontSize: 14, color: '#2e7d32', marginTop: 8 }}><strong>Nihai Ödeme (Elden)</strong>: Ödenecek + Bayram = <strong>{fmt(selected.nihai_odeme)} ₺</strong></div>
                {/* Kesinleştir'de PERSONEL giderine bu tam maliyet (brüt hakediş) yazılır;
                    resmi (banka) de elden (nakit) de işletme kasasından çıkar. */}
                <div style={{ fontSize: 14, color: '#1565c0', marginTop: 6, paddingTop: 8, borderTop: '1px dashed #cdd7e6' }}>
                  <strong>Tam Maliyet (İşletme)</strong>: <strong>{fmt(selected.tam_maliyet)} ₺</strong>
                  <div style={{ fontSize: 11, color: '#888', fontWeight: 400, marginTop: 2 }}>
                    Resmi/banka {fmt(selected.resmi_odeme)} + elden/nakit {fmt(selected.nihai_odeme)} — ikisi de işletme kasasından çıkar. Kesinleştir'de PERSONEL giderine bu tutar yazılır.
                  </div>
                </div>
              </div>

              {selected.izin_ozet && (selected.izin_ozet.yillik_gun > 0 || selected.izin_ozet.ucretsiz_gun > 0 || selected.izin_ozet.mazeret_gun > 0 || selected.izin_ozet.rapor_gun > 0) && (
                <div style={{ marginTop: 14, padding: 10, background: '#e1f5fe', borderLeft: '3px solid #0288d1', fontSize: 12 }}>
                  <strong>🏖️ Bu ay izinler:</strong>{' '}
                  {[
                    selected.izin_ozet.yillik_gun > 0 && `${selected.izin_ozet.yillik_gun} gün YILLIK`,
                    selected.izin_ozet.ucretsiz_gun > 0 && `${selected.izin_ozet.ucretsiz_gun} gün ÜCRETSİZ`,
                    selected.izin_ozet.mazeret_gun > 0 && `${selected.izin_ozet.mazeret_gun} gün MAZERET`,
                    selected.izin_ozet.rapor_gun > 0 && `${selected.izin_ozet.rapor_gun} gün RAPOR`,
                  ].filter(Boolean).join(', ')}
                  {' '}(çalışma: {selected.calisma_gun}/30)
                </div>
              )}

              {selected.notlar && (
                <div style={{ marginTop: 14, padding: 10, background: '#fffef0', borderLeft: '3px solid #fbc02d', fontSize: 12 }}>
                  <strong>Not:</strong> {selected.notlar}
                </div>
              )}

              <div style={{ marginTop: 14, paddingTop: 12, borderTop: '1px solid #eee', fontSize: 11, color: '#888' }}>
                Çalışma: {selected.calisma_gun || 0} gün · Borç toplam: {fmt(selected.borc_toplam)} ₺ · Fazla mesai: {selected.fazla_mesai_saat || 0} saat
              </div>
            </div>
          )}
        </div>

        {newModalOpen && (
          <NewPuantajModal
            ay={ay}
            personeller={personeller}
            fazlaMesaiCarpani={fazlaMesaiCarpani}
            onClose={() => setNewModalOpen(false)}
            onSaved={() => { setNewModalOpen(false); fetchData(); }}
          />
        )}

        {avansModalPuantaj && (
          <AvansModal
            puantaj={avansModalPuantaj}
            ay={ay}
            onClose={() => setAvansModalPuantaj(null)}
            onChanged={() => { fetchData(); }}
          />
        )}

        {hizliModalOpen && (
          <HizliPuantajModal
            ay={ay}
            lokasyonlar={lokasyonlar}
            fazlaMesaiCarpani={fazlaMesaiCarpani}
            onClose={() => setHizliModalOpen(false)}
            onSaved={() => { setHizliModalOpen(false); fetchData(); }}
          />
        )}
      </div>
    </div>
  );
};

// ============ Avans Modal (tarih bazlı audit) ============
const AvansModal = ({ puantaj, ay, onClose, onChanged }) => {
  const toast = useToast();
  const [avansListesi, setAvansListesi] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newTarih, setNewTarih] = useState(() => {
    // Default: ayın 15'i
    return `${ay}-15`;
  });
  const [newTutar, setNewTutar] = useState('');
  const [newAciklama, setNewAciklama] = useState('');
  const [saving, setSaving] = useState(false);
  const token = localStorage.getItem('access_token');

  const fetchAvanslar = () => {
    setLoading(true);
    fetch(`${API_URL}/api/avans?personel_id=${puantaj.personel_id}&ay=${ay}`, {
      headers: { 'Authorization': `Bearer ${token}` }
    })
      .then(r => r.ok ? r.json() : [])
      .then(d => { setAvansListesi(d); setLoading(false); });
  };

  React.useEffect(fetchAvanslar, [puantaj.personel_id, ay]); // eslint-disable-line

  const handleEkle = async () => {
    if (!newTutar || parseDecimal(newTutar) <= 0) { toast.warning('Geçerli tutar gir'); return; }
    setSaving(true);
    try {
      const r = await fetch(`${API_URL}/api/avans`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          personel_id: puantaj.personel_id,
          tarih: newTarih,
          tutar: parseDecimal(newTutar),
          aciklama: newAciklama || null,
        }),
      });
      if (r.ok) {
        toast.success('Avans eklendi');
        setNewTutar(''); setNewAciklama('');
        fetchAvanslar();
        onChanged();
      } else {
        const e = await r.json().catch(() => ({}));
        toast.error(e.detail || 'Eklenemedi');
      }
    } catch { toast.error('Bağlantı hatası'); }
    setSaving(false);
  };

  const handleSil = async (id) => {
    if (!window.confirm('Bu avans kaydı silinecek.')) return;
    const r = await fetch(`${API_URL}/api/avans/${id}`, {
      method: 'DELETE',
      headers: { 'Authorization': `Bearer ${token}` }
    });
    if (r.ok) { toast.success('Silindi'); fetchAvanslar(); onChanged(); }
    else toast.error('Silinemedi');
  };

  const toplam = avansListesi.reduce((s, a) => s + (a.tutar || 0), 0);
  const input = { width: '100%', padding: '8px 10px', border: `1px solid ${colors.border}`, borderRadius: radius.sm, fontSize: 13, boxSizing: 'border-box' };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }} onClick={onClose}>
      <div style={{ background: 'white', padding: 24, borderRadius: radius.lg, width: 520, maxWidth: '92vw', maxHeight: '90vh', overflowY: 'auto' }} onClick={e => e.stopPropagation()}>
        <h3 style={{ margin: '0 0 4px 0' }}>💰 Avans Kayıtları</h3>
        <div style={{ fontSize: 13, color: '#666', marginBottom: 16 }}>
          {puantaj.personel_ad} · {ay} · Toplam: <strong style={{ color: '#6a1b9a' }}>{toplam.toLocaleString('tr-TR')} ₺</strong>
        </div>

        {loading ? <div>Yükleniyor...</div> : (
          <>
            {avansListesi.length === 0 ? (
              <div style={{ padding: 20, textAlign: 'center', color: '#888', background: '#f8f8f8', borderRadius: 6, marginBottom: 16 }}>
                Henüz avans kaydı yok
              </div>
            ) : (
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12, marginBottom: 16 }}>
                <thead>
                  <tr style={{ background: '#f5f5f5' }}>
                    <th style={{ padding: '8px 10px', textAlign: 'left' }}>Tarih</th>
                    <th style={{ padding: '8px 10px', textAlign: 'right' }}>Tutar</th>
                    <th style={{ padding: '8px 10px', textAlign: 'left' }}>Açıklama</th>
                    <th style={{ padding: '8px 10px' }}></th>
                  </tr>
                </thead>
                <tbody>
                  {avansListesi.map(a => (
                    <tr key={a.id} style={{ borderBottom: '1px solid #f0f0f0' }}>
                      <td style={{ padding: '8px 10px' }}>{a.tarih}</td>
                      <td style={{ padding: '8px 10px', textAlign: 'right', fontFamily: 'monospace', fontWeight: 600 }}>{(a.tutar || 0).toLocaleString('tr-TR')} ₺</td>
                      <td style={{ padding: '8px 10px', fontSize: 11, color: '#666' }}>{a.aciklama || '-'}</td>
                      <td style={{ padding: '8px 10px' }}>
                        <button onClick={() => handleSil(a.id)} style={{ padding: '3px 8px', fontSize: 10, background: '#f8d7da', color: '#721c24', border: 'none', borderRadius: 3, cursor: 'pointer' }}>Sil</button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}

            <div style={{ padding: 14, background: '#f0f4ff', borderRadius: 6 }}>
              <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 10 }}>+ Yeni Avans</div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 8 }}>
                <div>
                  <div style={{ fontSize: 11, marginBottom: 3 }}>Tarih</div>
                  <input style={input} type="date" value={newTarih} onChange={e => setNewTarih(e.target.value)} />
                </div>
                <div>
                  <div style={{ fontSize: 11, marginBottom: 3 }}>Tutar (₺)</div>
                  <input style={input} type="text" inputMode="decimal" value={newTutar} onChange={e => setNewTutar(normalizeDecimalInput(e.target.value))} placeholder="ör. 2000" />
                </div>
              </div>
              <div style={{ fontSize: 11, marginBottom: 3 }}>Açıklama (opsiyonel)</div>
              <input style={input} value={newAciklama} onChange={e => setNewAciklama(e.target.value)} placeholder="ör. Acil nakit" />
              <button
                onClick={handleEkle}
                disabled={saving}
                style={{ marginTop: 10, padding: '8px 20px', background: styles.primary, color: 'white', border: 'none', borderRadius: 4, cursor: 'pointer', fontWeight: 600 }}
              >
                {saving ? 'Kaydediliyor...' : 'Avans Ekle'}
              </button>
            </div>
          </>
        )}

        <button onClick={onClose} style={{ marginTop: 16, padding: '8px 20px', background: '#666', color: 'white', border: 'none', borderRadius: 4, cursor: 'pointer', width: '100%' }}>Kapat</button>
      </div>
    </div>
  );
};

// Yeni satır modal
const NewPuantajModal = ({ ay, personeller, fazlaMesaiCarpani = 1.0, onClose, onSaved }) => {
  const toast = useToast();
  const [personelId, setPersonelId] = useState('');
  const [calismaGun, setCalismaGun] = useState(30);
  const [gelinenGun, setGelinenGun] = useState(26);
  const [avans, setAvans] = useState(0);
  const [ekMesai, setEkMesai] = useState(0);
  const [notlar, setNotlar] = useState('');
  const [saving, setSaving] = useState(false);
  const token = localStorage.getItem('access_token');

  // Seçili personel → varsayılan maaş/yol/resmi/tip ile salt-okunur önizleme
  const secili = personeller.find(p => String(p.id) === String(personelId));

  // Faz F: salt-okunur tahmini önizleme (kaydetme payload'ını ETKİLEMEZ — backend kesin hesaplar)
  const calismaGunNum = parseDecimal(calismaGun, 0);
  const gelinenGunNum = parseDecimal(gelinenGun, 0);
  const ekMesaiNum = parseDecimal(ekMesai, 0);
  const avansNum = parseDecimal(avans, 0);
  const netMaas = secili ? (secili.varsayilan_brut_maas || 0) : 0;
  const gunlukYol = secili ? (secili.gunluk_yol_ucreti || 0) : 0;
  const gunlukSaat = secili ? (Number(secili.gunluk_calisma_saati) || 8) : 8;
  const maasTl = Math.round(netMaas * calismaGunNum / 30 * 100) / 100;
  const yolTl = Math.round(gelinenGunNum * gunlukYol * 100) / 100;
  const mesaiTl = Math.round((netMaas / (30 * gunlukSaat)) * ekMesaiNum * (fazlaMesaiCarpani || 1.0) * 100) / 100;
  const resmiTl = secili && secili.tip === 'KADRO'
    ? Math.round((secili.varsayilan_resmi_odeme || 0) * Math.min(calismaGunNum, 30) / 30 * 100) / 100
    : 0;
  const nihaiTl = Math.round((maasTl + yolTl + mesaiTl - resmiTl - avansNum) * 100) / 100;
  const fmtP = (n) => (n || 0).toLocaleString('tr-TR', { maximumFractionDigits: 0 });

  const handleSave = async () => {
    if (!personelId) { toast.warning('Personel seç'); return; }
    setSaving(true);
    try {
      const r = await fetch(`${API_URL}/api/puantaj`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          personel_id: parseInt(personelId, 10),
          ay: `${ay}-01`,
          calisma_gun: parseDecimal(calismaGun) || 30,
          gelinen_gun: parseDecimal(gelinenGun) || 26,
          avans: parseDecimal(avans) || 0,
          fazla_mesai_saat: parseDecimal(ekMesai) || 0,
          notlar: notlar || null,
          // yol_yardimi / resmi_odeme / aylik_brut_maas GÖNDERİLMİYOR → backend personel varsayılanından + gelinen_gun ile türetir
        }),
      });
      if (r.ok) { toast.success('Eklendi'); onSaved(); }
      else {
        const err = await r.json().catch(() => ({}));
        toast.error(err.detail || 'Eklenemedi');
      }
    } catch {
      toast.error('Bağlantı hatası');
    }
    setSaving(false);
  };

  const fieldLabel = { display: 'block', fontSize: 12, marginTop: 10, fontWeight: 600 };
  const fieldInput = { width: '100%', padding: 8, border: '1px solid #ccc', borderRadius: 4, fontSize: 14, boxSizing: 'border-box' };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }} onClick={onClose}>
      <div style={{ background: 'white', padding: 20, borderRadius: 8, width: 460, maxWidth: '94vw', maxHeight: '92vh', overflowY: 'auto' }} onClick={e => e.stopPropagation()}>
        <h3 style={{ marginTop: 0 }}>Yeni Puantaj Satırı · {ay}</h3>
        <label style={fieldLabel}>Personel *</label>
        <select value={personelId} onChange={e => setPersonelId(e.target.value)} style={fieldInput}>
          <option value="">(Seçiniz)</option>
          {personeller.map(p => <option key={p.id} value={p.id}>{p.ad_soyad} ({p.pozisyon || '-'})</option>)}
        </select>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
          <div>
            <label style={fieldLabel}>Çalışma Gün</label>
            <input type="text" inputMode="decimal" value={calismaGun} onChange={e => setCalismaGun(normalizeDecimalInput(e.target.value))} style={fieldInput} />
          </div>
          <div>
            <label style={fieldLabel}>Gelinen Gün</label>
            <input type="text" inputMode="decimal" value={gelinenGun} onChange={e => setGelinenGun(normalizeDecimalInput(e.target.value))} style={fieldInput} />
          </div>
        </div>
        <div style={{ fontSize: 11, color: '#888', marginTop: 4 }}>
          Çal. gün 30 varsayılan (hafta izni kullanılmadıysa 34'e kadar). Gelinen Gün 26 varsayılan — yol = Gelinen Gün × günlük yol ücreti.
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
          <div>
            <label style={fieldLabel}>Avans (₺)</label>
            <input type="text" inputMode="decimal" value={avans} onChange={e => setAvans(normalizeDecimalInput(e.target.value))} style={fieldInput} placeholder="0" />
          </div>
          <div>
            <label style={fieldLabel}>Ek Mesai (saat)</label>
            <input type="text" inputMode="decimal" value={ekMesai} onChange={e => setEkMesai(normalizeDecimalInput(e.target.value))} style={fieldInput} placeholder="0" />
          </div>
        </div>

        <label style={fieldLabel}>Not</label>
        <input value={notlar} onChange={e => setNotlar(e.target.value)} style={fieldInput} placeholder="Opsiyonel açıklama" />

        {secili && (
          <div style={{ marginTop: 14, padding: 12, background: '#eef4ff', border: '1px solid #c7d8f5', borderRadius: 6 }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: '#1565c0', marginBottom: 8 }}>≈ Tahmini Önizleme</div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '4px 16px', fontSize: 12, color: '#333', fontFamily: 'monospace' }}>
              <div>Maaş: <strong>{fmtP(maasTl)} ₺</strong></div>
              <div>Yol: <strong style={{ color: '#2e7d32' }}>{fmtP(yolTl)} ₺</strong></div>
              <div>Resmi: <strong style={{ color: secili.tip === 'KADRO' ? '#c62828' : '#bbb' }}>{secili.tip === 'KADRO' ? `${fmtP(resmiTl)} ₺` : '—'}</strong></div>
              <div>Mesai: <strong style={{ color: '#1565c0' }}>{fmtP(mesaiTl)} ₺</strong></div>
            </div>
            <div style={{ marginTop: 8, paddingTop: 8, borderTop: '1px solid #c7d8f5', fontSize: 13, fontFamily: 'monospace' }}>
              Nihai ≈ <strong style={{ color: nihaiTl < 0 ? '#c62828' : '#2e7d32' }}>{fmtP(nihaiTl)} ₺</strong>
            </div>
            <div style={{ marginTop: 6, fontSize: 10, color: '#9e7b00' }}>
              ≈ tahmini (kesin değer kayıtta hesaplanır)
            </div>
          </div>
        )}

        <div style={{ marginTop: 14, display: 'flex', gap: 8 }}>
          <button onClick={handleSave} disabled={saving} style={{ flex: 1, padding: 10, background: '#2e7d32', color: 'white', border: 'none', borderRadius: 4, cursor: saving ? 'not-allowed' : 'pointer' }}>
            {saving ? 'Ekleniyor...' : 'Kaydet'}
          </button>
          <button onClick={onClose} style={{ flex: 1, padding: 10, background: '#666', color: 'white', border: 'none', borderRadius: 4, cursor: 'pointer' }}>İptal</button>
        </div>
        <div style={{ marginTop: 10, fontSize: 11, color: '#888' }}>
          Kayıt sonrası personelin varsayılan maaş + yol değerleri otomatik gelir. Sonra tabloda düzenleyin.
        </div>
      </div>
    </div>
  );
};

export default PuantajAylikPage;
